import React, { useEffect, useMemo, useState } from "react";
import { Search, Plus, Download, Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError, downloadFile } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { PageHeader, Panel } from "../lib/ui";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Switch } from "../components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";

const num = (n) => (Number(n) || 0).toLocaleString("id-ID");

export default function MasterSimple({ kind, title, hasCode }) {
  const { can } = useAuth();
  const canWrite = can("warehouse_admin");
  const [q, setQ] = useState("");
  const [debounced, setDebounced] = useState("");
  const [active, setActive] = useState("all");
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [data, setData] = useState({ items: [], total: 0, page: 1, pages: 1 });
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(undefined); // undefined=closed, null=create, obj=edit

  useEffect(() => { const t = setTimeout(() => { setDebounced(q); setPage(1); }, 350); return () => clearTimeout(t); }, [q]);

  const params = useMemo(() => ({ q: debounced || undefined, active: active !== "all" ? active : undefined, page, limit }), [debounced, active, page, limit]);
  const load = () => {
    setLoading(true);
    api.get(`/masters/${kind}`, { params }).then((r) => setData(r.data)).catch(() => toast.error("Gagal memuat")).finally(() => setLoading(false));
  };
  useEffect(load, [params, kind]);

  const doExport = () => downloadFile(`/masters/${kind}/export`, { q: params.q, active: params.active }, `${kind}.xlsx`).catch(() => toast.error("Gagal export"));

  const doDelete = async (row) => {
    if (!window.confirm(`Hapus ${title} "${row.name}"?`)) return;
    try { await api.delete(`/masters/${kind}/${row.id}`); toast.success("Dihapus"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const start0 = (data.page - 1) * limit;

  return (
    <div data-testid={`master-${kind}-page`}>
      <PageHeader title={title} subtitle={`Master ${title}`}>
        {canWrite && <Button onClick={() => setEditing(null)} className="bg-cocoa hover:bg-cocoa-dark text-ivory" data-testid={`${kind}-create-btn`}><Plus className="w-4 h-4 mr-1.5" />Create</Button>}
        <Button variant="outline" onClick={doExport} data-testid={`${kind}-export-btn`}><Download className="w-4 h-4 mr-1.5" />Export</Button>
      </PageHeader>

      <Panel className="p-0">
        <div className="flex flex-wrap items-end gap-3 px-4 py-3 border-b border-taupe/15">
          <div className="relative w-64">
            <Search className="absolute left-2.5 top-2.5 w-4 h-4 text-wmuted" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder={`Cari ${title.toLowerCase()}...`} className="pl-8 h-9" data-testid={`${kind}-search`} />
          </div>
          <div>
            <Label className="text-xs">Status</Label>
            <Select value={active} onValueChange={(v) => { setActive(v); setPage(1); }}>
              <SelectTrigger className="h-9 w-36" data-testid={`${kind}-filter-active`}><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="all">Semua</SelectItem><SelectItem value="active">Aktif</SelectItem><SelectItem value="inactive">Nonaktif</SelectItem></SelectContent>
            </Select>
          </div>
        </div>

        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-12">No</TableHead>
              <TableHead>Name</TableHead>
              {hasCode && <TableHead>Kode</TableHead>}
              <TableHead className="text-center">Dipakai</TableHead>
              <TableHead className="text-center">Status</TableHead>
              <TableHead className="text-right">Action</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={hasCode ? 6 : 5} className="text-center py-10 text-wmuted">Memuat...</TableCell></TableRow>
            ) : data.items.length === 0 ? (
              <TableRow><TableCell colSpan={hasCode ? 6 : 5} className="text-center py-10 text-wmuted">Belum ada data</TableCell></TableRow>
            ) : data.items.map((row, i) => (
              <TableRow key={row.id} data-testid={`${kind}-row-${row.id}`}>
                <TableCell className="text-wmuted">{start0 + i + 1}</TableCell>
                <TableCell className="font-medium text-cocoa">{row.name}</TableCell>
                {hasCode && <TableCell className="text-sm text-wmuted">{row.code || "-"}</TableCell>}
                <TableCell className="text-center">{num(row.usage)}</TableCell>
                <TableCell className="text-center">
                  <Badge variant="outline" className={row.is_active ? "border-green-300 text-green-700" : "border-slate-300 text-slate-400"}>{row.is_active ? "Aktif" : "Nonaktif"}</Badge>
                </TableCell>
                <TableCell className="text-right whitespace-nowrap">
                  {canWrite && <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => setEditing(row)} data-testid={`${kind}-edit-${row.id}`}><Pencil className="w-4 h-4" /></Button>}
                  {canWrite && <Button size="icon" variant="ghost" className="h-8 w-8 text-status-reject" onClick={() => doDelete(row)} data-testid={`${kind}-delete-${row.id}`}><Trash2 className="w-4 h-4" /></Button>}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>

        <div className="flex flex-col sm:flex-row items-center justify-between gap-3 px-4 py-3 border-t border-taupe/15 text-sm">
          <div className="flex items-center gap-2 text-wmuted">
            <span>Show</span>
            <Select value={String(limit)} onValueChange={(v) => { setLimit(Number(v)); setPage(1); }}>
              <SelectTrigger className="h-8 w-20" data-testid={`${kind}-limit`}><SelectValue /></SelectTrigger>
              <SelectContent>{[10, 25, 50, 100].map((n) => <SelectItem key={n} value={String(n)}>{n}</SelectItem>)}</SelectContent>
            </Select>
            <span>entries · Menampilkan {data.total === 0 ? 0 : start0 + 1}–{Math.min(start0 + limit, data.total)} dari {data.total}</span>
          </div>
          <div className="flex items-center gap-1">
            <Button size="sm" variant="outline" disabled={data.page <= 1} onClick={() => setPage((p) => p - 1)} data-testid={`${kind}-prev`}>Previous</Button>
            <span className="px-3 text-wmuted">Hal {data.page} / {data.pages}</span>
            <Button size="sm" variant="outline" disabled={data.page >= data.pages} onClick={() => setPage((p) => p + 1)} data-testid={`${kind}-next`}>Next</Button>
          </div>
        </div>
      </Panel>

      {editing !== undefined && (
        <MasterForm kind={kind} title={title} hasCode={hasCode} row={editing} onClose={() => setEditing(undefined)} onSaved={() => { setEditing(undefined); load(); }} />
      )}
    </div>
  );
}

function MasterForm({ kind, title, hasCode, row, onClose, onSaved }) {
  const isEdit = !!row;
  const [name, setName] = useState(row?.name || "");
  const [code, setCode] = useState(row?.code || "");
  const [isActive, setIsActive] = useState(row ? !!row.is_active : true);
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (!name.trim()) return toast.error("Nama wajib diisi");
    const payload = { name: name.trim(), is_active: isActive };
    if (hasCode) payload.code = code.trim() || null;
    setSaving(true);
    try {
      if (isEdit) await api.put(`/masters/${kind}/${row.id}`, payload);
      else await api.post(`/masters/${kind}`, payload);
      toast.success(isEdit ? `${title} diperbarui` : `${title} dibuat`);
      onSaved();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid={`${kind}-form-dialog`}>
        <DialogHeader>
          <DialogTitle>{isEdit ? `Edit ${title}` : `Create ${title}`}</DialogTitle>
          <DialogDescription>Master data {title.toLowerCase()}.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div><Label>Nama *</Label><Input value={name} onChange={(e) => setName(e.target.value)} placeholder={`Nama ${title.toLowerCase()}`} data-testid={`${kind}-form-name`} autoFocus /></div>
          {hasCode && <div><Label>Kode (opsional)</Label><Input value={code} onChange={(e) => setCode(e.target.value)} data-testid={`${kind}-form-code`} /></div>}
          {isEdit && (
            <div className="flex items-center gap-3">
              <Switch checked={isActive} onCheckedChange={setIsActive} data-testid={`${kind}-form-active`} />
              <Label>Aktif</Label>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Batal</Button>
          <Button onClick={save} disabled={saving} className="bg-cocoa hover:bg-cocoa-dark text-ivory" data-testid={`${kind}-form-save`}>{saving ? "Menyimpan..." : (isEdit ? "Update" : "Simpan")}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
